import { useState } from "react";
import {
  Wand2, User as UserIcon, Brain, BookOpen, Trash2, Download, ChevronDown,
} from "lucide-react";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import CharacterFactsPanel from "@/components/CharacterFactsPanel";
import { STYLE_OPTIONS, STYLE_LABELS, LENGTH_OPTIONS, formatMonthReset } from "@/lib/chatConstants";
import { PersonaPicker } from "./PersonaPicker";
import { MemoryEditor } from "./MemoryEditor";
import { PinnedMessagesList } from "./PinnedMessagesList";

export function StoryPanel({
  open, onOpenChange,
  chat, chatId, personas, pinnedIds, setPinnedIds,
  changeStyle, changeLength, changeModel, changePersona,
  addMemory, delMemory,
  playRecap, usage,
  exporting, exportChat,
  deleteChat,
  user,
}) {
  const [exportOpen, setExportOpen] = useState(false);
  const character = chat.character || {};

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="glass-strong border-white/10 w-[380px] max-w-full text-white" data-testid="chat-side-panel">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl text-white">Story Panel</SheetTitle>
          <SheetDescription className="sr-only">Chat style, persona, remembered facts, and character facts for this conversation.</SheetDescription>
        </SheetHeader>

        <div className="mt-4 flex flex-col overflow-hidden" style={{ maxHeight: "calc(100vh - 90px)" }}>
          <Tabs defaultValue="style" className="flex min-h-0 flex-1 flex-col">
            <TabsList
              data-testid="panel-tabs"
              className="grid w-full grid-cols-4 rounded-full bg-white/[0.04] p-1 h-auto"
            >
              <TabsTrigger data-testid="panel-tab-style" value="style" className="rounded-full text-[11px] uppercase tracking-widest data-[state=active]:bg-violet-500/25 data-[state=active]:text-white gap-1 py-1.5">
                <Wand2 size={12} /> Style
              </TabsTrigger>
              <TabsTrigger data-testid="panel-tab-persona" value="persona" className="rounded-full text-[11px] uppercase tracking-widest data-[state=active]:bg-violet-500/25 data-[state=active]:text-white gap-1 py-1.5">
                <UserIcon size={12} /> Persona
              </TabsTrigger>
              <TabsTrigger data-testid="panel-tab-memory" value="memory" className="rounded-full text-[11px] uppercase tracking-widest data-[state=active]:bg-violet-500/25 data-[state=active]:text-white gap-1 py-1.5">
                <Brain size={12} /> Memory
              </TabsTrigger>
              <TabsTrigger data-testid="panel-tab-facts" value="facts" className="rounded-full text-[11px] uppercase tracking-widest data-[state=active]:bg-violet-500/25 data-[state=active]:text-white gap-1 py-1.5">
                <BookOpen size={12} /> Facts
              </TabsTrigger>
            </TabsList>

            <div className="mt-4 min-h-0 flex-1 overflow-y-auto pr-1">
              <TabsContent value="style" className="mt-0 space-y-4">
                {/* Style — collapsed into a single dropdown so it doesn't
                    dominate the panel. The full 7-option list is still there,
                    just tucked behind the Select. */}
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Style</div>
                    {!STYLE_LABELS[chat.chat_style] && (
                      <div data-testid="style-unset-hint" className="text-[10px] text-violet-300/80">Optional — pick one to shape the tone</div>
                    )}
                  </div>
                  <Select
                    value={chat.chat_style || "__none__"}
                    onValueChange={(v) => changeStyle(v === "__none__" ? null : v)}
                  >
                    <SelectTrigger
                      data-testid="panel-style-select"
                      className="w-full rounded-xl border-white/10 bg-white/[0.02] hover:bg-white/[0.05] text-sm h-auto py-2.5"
                    >
                      <SelectValue placeholder="Pick a style…" />
                    </SelectTrigger>
                    <SelectContent className="glass-strong border-white/10 max-h-[60vh]" data-testid="panel-style-options">
                      <SelectItem value="__none__" className="text-neutral-400" data-testid="panel-style-none">
                        <span className="italic">No style — free-form</span>
                      </SelectItem>
                      {STYLE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.id} value={opt.id} data-testid={`panel-style-${opt.id}`}>
                          <div className="flex flex-col">
                            <span className="text-sm text-white">{opt.title}</span>
                            <span className="text-[11px] text-neutral-400">{opt.desc}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Advanced settings — response length + admin model picker
                    live behind a collapsible header so the panel stays clean
                    by default. */}
                <Collapsible data-testid="panel-advanced-collapsible">
                  <CollapsibleTrigger
                    data-testid="panel-advanced-trigger"
                    className="group flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5 text-left hover:bg-white/[0.05] transition"
                  >
                    <span className="text-[11px] uppercase tracking-widest text-neutral-300">Advanced settings</span>
                    <ChevronDown
                      size={14}
                      className="text-neutral-400 transition-transform group-data-[state=open]:rotate-180"
                    />
                  </CollapsibleTrigger>
                  <CollapsibleContent data-testid="panel-advanced-content">
                    <div className="mt-3 space-y-4">
                      <div>
                        <div className="mb-2 text-[10px] uppercase tracking-widest text-neutral-500">Response length</div>
                        <div className="grid grid-cols-3 gap-2" data-testid="length-picker">
                          {LENGTH_OPTIONS.map((opt) => {
                            const active = (chat.response_length || "balanced") === opt.id;
                            return (
                              <button
                                key={opt.id}
                                type="button"
                                data-testid={`panel-length-${opt.id}`}
                                onClick={() => changeLength(opt.id)}
                                className={`rounded-xl border p-2 text-center transition ${
                                  active
                                    ? "border-violet-500/50 bg-violet-500/10"
                                    : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"
                                }`}
                              >
                                <div className="text-xs font-medium text-white">{opt.title}</div>
                                <div className="mt-0.5 text-[10px] text-neutral-400 leading-tight">{opt.desc}</div>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {user?.is_admin && (
                        <div data-testid="model-picker-container">
                          <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500">
                            Model
                            <span className="rounded-full bg-amber-500/20 border border-amber-400/40 px-1.5 py-[1px] text-[8px] tracking-normal text-amber-200 normal-case">
                              Admin · Dev
                            </span>
                          </div>
                          <div className="grid grid-cols-3 gap-2" data-testid="model-picker">
                            {[
                              { id: "claude-sonnet-4-6", title: "Sonnet 4.6", desc: "Claude · default" },
                              { id: "claude-opus-4-8", title: "Opus 4.8", desc: "Claude · pricier" },
                              { id: "deepseek-chat", title: "DeepSeek V3", desc: "Uncensored · opt-in" },
                            ].map((opt) => {
                              const active = (chat.llm_model || "claude-sonnet-4-6") === opt.id;
                              return (
                                <button
                                  key={opt.id}
                                  type="button"
                                  data-testid={`panel-model-${opt.id}`}
                                  onClick={() => changeModel(opt.id)}
                                  className={`rounded-xl border p-2 text-left transition ${
                                    active
                                      ? "border-amber-400/50 bg-amber-500/10"
                                      : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"
                                  }`}
                                >
                                  <div className="text-xs font-medium text-white">{opt.title}</div>
                                  <div className="mt-0.5 text-[10px] text-neutral-400 leading-tight">{opt.desc}</div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </TabsContent>

              <TabsContent value="persona" className="mt-0">
                <PersonaPicker
                  personas={personas}
                  selectedId={chat.persona_id || ""}
                  onChange={changePersona}
                />
              </TabsContent>

              <TabsContent value="memory" className="mt-0">
                <PinnedMessagesList chatId={chatId} pinnedIds={pinnedIds} onUnpin={async (mid) => {
                  const { data } = await api.delete(`/chats/${chatId}/pin-message/${mid}`);
                  setPinnedIds(data.pinned_message_ids);
                }} />
                <div className="mt-3 mb-1 text-[10px] uppercase tracking-widest text-neutral-500">Free-form notes</div>
                <MemoryEditor
                  memories={chat.pinned_memories || []}
                  onAdd={addMemory}
                  onDelete={delMemory}
                />
                {chat.memory_summary && (
                  <div className="mt-3 rounded-xl border border-white/5 bg-white/[0.02] p-3">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Auto summary</div>
                    <p className="text-xs text-neutral-300 whitespace-pre-wrap">{chat.memory_summary}</p>
                  </div>
                )}
                <div className="mt-4 rounded-xl border border-white/5 bg-white/[0.02] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Story Recap</div>
                  <p className="text-xs text-neutral-400 mb-3">
                    Get a cinematic summary of the whole chat so far — great for coming back to a long story.
                  </p>
                  <button
                    data-testid="recap-generate-btn"
                    onClick={playRecap}
                    disabled={usage && usage.recaps_this_month >= usage.recaps_limit}
                    className="btn-pill btn-primary text-xs px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <BookOpen size={12} /> Generate recap
                  </button>
                  {usage && (
                    <div data-testid="recap-usage-line" className="mt-2 text-[10px] text-neutral-500">
                      <span className={usage.recaps_this_month >= usage.recaps_limit ? "text-rose-300" : ""}>
                        {usage.recaps_this_month}/{usage.recaps_limit} recaps used this month
                      </span>
                      {" · "}resets {formatMonthReset()}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="facts" className="mt-0">
                <CharacterFactsPanel chatId={chatId} mainCharacterName={character.name} />
              </TabsContent>
            </div>
          </Tabs>

          <div data-testid="panel-export-container" className="mt-4">
            <button
              data-testid="panel-export-chat-btn"
              onClick={() => setExportOpen((v) => !v)}
              disabled={exporting}
              className="btn-pill w-full bg-violet-500/10 border border-violet-500/30 text-violet-100 hover:bg-violet-500/15 disabled:opacity-50"
              title="Immersive+ perk"
            >
              <Download size={14} /> {exporting ? "Preparing…" : "Export chat"}
            </button>
            {exportOpen && (
              <div className="mt-2 grid grid-cols-3 gap-1.5" data-testid="panel-export-choices">
                <button
                  data-testid="panel-export-pdf-btn"
                  onClick={() => { setExportOpen(false); exportChat("pdf"); }}
                  className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-2.5 text-left hover:bg-violet-500/10"
                >
                  <div className="text-xs font-medium text-white">PDF</div>
                  <div className="mt-0.5 text-[10px] text-neutral-400 leading-tight">Paperback · read like a story</div>
                </button>
                <button
                  data-testid="panel-export-html-btn"
                  onClick={() => { setExportOpen(false); exportChat("html"); }}
                  className="rounded-xl border border-white/10 bg-white/[0.02] p-2.5 text-left hover:bg-white/[0.05]"
                >
                  <div className="text-xs font-medium text-white">HTML</div>
                  <div className="mt-0.5 text-[10px] text-neutral-400 leading-tight">Styled · print or web</div>
                </button>
                <button
                  data-testid="panel-export-md-btn"
                  onClick={() => { setExportOpen(false); exportChat("md"); }}
                  className="rounded-xl border border-white/10 bg-white/[0.02] p-2.5 text-left hover:bg-white/[0.05]"
                >
                  <div className="text-xs font-medium text-white">Markdown</div>
                  <div className="mt-0.5 text-[10px] text-neutral-400 leading-tight">Raw · Notion / GitHub</div>
                </button>
              </div>
            )}
          </div>

          <button
            data-testid="panel-delete-chat-btn"
            onClick={deleteChat}
            className="btn-pill mt-2 w-full bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500/15"
          >
            <Trash2 size={14} /> Delete chat
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
